import { useSeatPhones, type SeatPhone } from '../../lib/api/hooks-extension.js';
import { Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { relativeTime } from '../../lib/format.js';

/**
 * Every phone signed in for one seat, opened from its row (owner, 23 September 2026: "how do I
 * know if they are online or not").
 *
 * A seat's badge can only be about the line it answers as. With an app registered through the
 * push server beside a desk phone on the seat's own login, the seat read as online on the push
 * server and the handset went unmentioned. Here each name stands on its own: what it is, whether
 * it is registered now, from where, and when it was last seen if it is not.
 */
export function SeatPhones({ tenantId, extensionId }: { tenantId: string; extensionId: string }) {
  const q = useSeatPhones(tenantId, extensionId);

  if (q.data === undefined) {
    return <div className="arc-skeleton" style={{ height: 68, margin: 12 }} />;
  }
  if (!q.data.known) {
    return (
      <p className="arc-subtle arc-seat-phones-note">
        No node has reported on this account, so nothing is known about its phones.
      </p>
    );
  }
  if (q.data.data.length === 0) {
    return <p className="arc-subtle arc-seat-phones-note">This extension has no phones set up yet.</p>;
  }

  return (
    <div className="arc-seat-phones">
      {q.data.data.map((p) => <Phone key={p.username} p={p} />)}
    </div>
  );
}

/** What a name is, in the words the portal uses elsewhere. */
const KIND: Record<SeatPhone['kind'], string> = {
  extension: 'This extension’s own login',
  registration: 'Additional registration',
  app: 'ArcSIP app',
  handset: 'Desk phone',
};

function Phone({ p }: { p: SeatPhone }) {
  const state = !p.registered ? 'offline' : p.reachable ? 'online' : 'unreachable';
  return (
    <div className="arc-seat-phone">
      <span className="arc-seat-phone-dot" data-state={state} aria-hidden="true" />
      <div className="arc-seat-phone-name">
        <span className="arc-mono" style={{ fontWeight: 550 }}>{p.username}</span>
        <span className="arc-subtle" style={{ fontSize: 11.5 }}>
          {p.label ? `${p.label} · ` : ''}{KIND[p.kind]}
          {p.ring_enabled === false ? ' · does not ring' : ''}
        </span>
      </div>
      <div className="arc-seat-phone-state">
        {state === 'online' ? <Badge tone="ok">online</Badge>
          : state === 'unreachable' ? <Badge tone="warn">not answering</Badge>
            : <Badge tone="muted">offline</Badge>}
        {p.via_push && (
          <span title="The push server holds this registration and wakes the phone for a call.">
            <Badge tone="info">push</Badge>
          </span>
        )}
      </div>
      <div className="arc-seat-phone-where arc-subtle">
        {p.registered
          ? <>
            {p.ip ? <span className="arc-mono">{p.ip}</span> : <span>—</span>}
            {p.transport ? <span> · {p.transport.toUpperCase()}</span> : null}
            {p.ping_ms !== null ? <span> · {Math.round(p.ping_ms)} ms</span> : null}
          </>
          : p.offline_since || p.last_seen_at
            ? <span>last seen {relativeTime(p.offline_since ?? p.last_seen_at!)}</span>
            : <span>never registered</span>}
      </div>
      <div className="arc-seat-phone-ua arc-subtle arc-truncate" title={p.user_agent ?? undefined}>
        {p.user_agent ?? ''}
      </div>
      {p.registered && <Icon name="phone" size={13} />}
    </div>
  );
}
